// Composer footer: a single rounded input with an embedded send button.
//
// ComposerPrimitive supplies all the state (text content, Enter-to-send,
// disabled while running). We provide the look, the visible affordances,
// and the autosize signal that lets the thread reserve bottom-padding
// equal to the composer's current height.
//
// Height tracking: a ResizeObserver on the wrapper publishes the composer's
// rendered height to CSS var --copilot-composer-h on the closest .copilot-root
// ancestor. The thread viewport consumes that var via padding-bottom so the
// last message never hides under the composer as the textarea grows.

import React, { useEffect, useRef } from "react";
import { ComposerPrimitive, ThreadPrimitive } from "@assistant-ui/react";
import { SendHorizontalIcon, SquareIcon } from "lucide-react";

export function Composer() {
  const wrapRef = useRef(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap || typeof ResizeObserver === "undefined") return;
    const panel = wrap.closest(".copilot-root");
    if (!panel) return;
    // Defer the CSS-var write via requestAnimationFrame and dedupe on the
    // last known height. Writing inside the ResizeObserver callback triggers
    // layout (viewport padding-bottom + feather height both depend on this
    // var), which the same observer then picks up — Chrome flags this as
    // "ResizeObserver loop completed with undelivered notifications". rAF
    // pushes the write to the next frame and the dedupe avoids fire-storms
    // when the textarea height isn't actually changing.
    let lastH = -1;
    let rafId = 0;
    const update = () => {
      const h = wrap.offsetHeight;
      if (h === lastH) return;
      lastH = h;
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        panel.style.setProperty("--copilot-composer-h", `${h}px`);
        rafId = 0;
      });
    };
    const ro = new ResizeObserver(update);
    ro.observe(wrap);
    update();
    return () => {
      ro.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <div ref={wrapRef} className="copilot-composer-wrap">
      <ComposerPrimitive.Root className="tw-relative tw-flex tw-w-full tw-items-end tw-gap-2 tw-rounded-2xl tw-border tw-border-border tw-bg-input tw-px-4 tw-py-3 tw-shadow-inner focus-within:tw-border-ring focus-within:tw-ring-2 focus-within:tw-ring-ring/40">
        <ComposerPrimitive.Input
          autoFocus
          rows={1}
          placeholder="Tell the copilot what to do…"
          aria-label="Copilot message"
          className="tw-min-h-[27px] tw-max-h-48 tw-flex-1 tw-resize-none tw-bg-transparent tw-text-left tw-text-[14px] tw-leading-6 tw-text-foreground tw-outline-none placeholder:tw-text-muted-foreground disabled:tw-cursor-not-allowed disabled:tw-opacity-60"
        />
        {/* Send while idle, Cancel while a turn is in flight. assistant-ui's
            ThreadPrimitive.If is the canonical predicate for the thread-level
            isRunning state — feeding directly off the runtime adapter, so
            controller.busy flipping flips this swap. The Cancel primitive
            calls onCancel from the runtime config, which lands on
            controller.cancel() — see CopilotRuntimeProvider.jsx. */}
        <ThreadPrimitive.If running={false}>
          <ComposerPrimitive.Send
            aria-label="Send message"
            className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-full tw-bg-copilot-primary tw-text-copilot-primary-foreground tw-transition hover:tw-bg-copilot-primary/90 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
          >
            <SendHorizontalIcon className="tw-h-4 tw-w-4" />
          </ComposerPrimitive.Send>
        </ThreadPrimitive.If>
        <ThreadPrimitive.If running>
          <ComposerPrimitive.Cancel
            aria-label="Cancel turn"
            title="Cancel current action"
            className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-full tw-bg-destructive tw-text-destructive-foreground tw-transition hover:tw-bg-destructive/90 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
          >
            <SquareIcon className="tw-h-3.5 tw-w-3.5 tw-fill-current" />
          </ComposerPrimitive.Cancel>
        </ThreadPrimitive.If>
      </ComposerPrimitive.Root>
      <div className="copilot-composer-hint" aria-hidden="true">
        <span>
          <kbd>↵</kbd> send
        </span>
        <span className="copilot-composer-hint-sep" />
        <span>
          <kbd>⇧</kbd>+<kbd>↵</kbd> newline
        </span>
      </div>
    </div>
  );
}
